import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/Layout';
import * as aiConfigService from '../services/aiConfigurationService';
import type { AIConfiguration, CreateAIConfigurationInput, UpdateAIConfigurationInput } from '../services/aiConfigurationService';

/**
 * Field Information Tooltip Component
 */
interface FieldInfoProps {
  title: string;
  description: string;
  examples?: string[];
  notes?: string[];
}

function FieldInfo({ title, description, examples, notes }: FieldInfoProps): React.ReactElement {
  const [showModal, setShowModal] = useState(false);

  return (
    <>
      <button
        type="button"
        onClick={() => setShowModal(true)}
        className="ml-2 inline-flex items-center justify-center w-5 h-5 text-indigo-600 hover:text-indigo-800 focus:outline-none"
        title="Click for more information"
      >
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
        </svg>
      </button>

      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75 overflow-y-auto h-full w-full z-[100]" onClick={() => setShowModal(false)}>
          <div className="relative top-20 mx-auto p-6 border w-full max-w-2xl shadow-2xl rounded-lg bg-white" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-xl font-bold text-gray-900">{title}</h3>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowModal(false);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Description</h4>
                <p className="text-sm text-gray-600">{description}</p>
              </div>

              {examples && examples.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Examples</h4>
                  <ul className="list-disc list-inside space-y-1">
                    {examples.map((example, index) => (
                      <li key={index} className="text-sm text-gray-600">{example}</li>
                    ))}
                  </ul>
                </div>
              )}

              {notes && notes.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Important Notes</h4>
                  <ul className="list-disc list-inside space-y-1">
                    {notes.map((note, index) => (
                      <li key={index} className="text-sm text-gray-600">{note}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <div className="mt-6 flex justify-end">
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowModal(false);
                }}
                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/**
 * AI Configuration Management Page
 * Platform Admin only
 */
export function AIConfigurationPage(): React.ReactElement {
  const { token, user } = useAuth();
  const [configurations, setConfigurations] = useState<AIConfiguration[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState<AIConfiguration | null>(null);

  // Form data
  const [formData, setFormData] = useState<CreateAIConfigurationInput>({
    name: '',
    description: '',
    configuration_type: 'executive_summary',
    model: 'gpt-3.5-turbo',
    temperature: 0.3,
    max_tokens: 2000,
    system_prompt: '',
    user_prompt_template: '',
    min_requirements: 3,
    max_requirements_per_type: 10,
    requirement_description_max_length: 200
  });

  useEffect(() => {
    loadConfigurations();
  }, []);

  const loadConfigurations = async () => {
    if (!token) return;

    setLoading(true);
    try {
      const configs = await aiConfigService.getAllConfigurations(token);
      setConfigurations(configs);
      setError('');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load configurations');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!token) return;

    try {
      await aiConfigService.createConfiguration(formData, token);
      setShowCreateModal(false);
      setSuccessMessage('Configuration created successfully');
      setTimeout(() => setSuccessMessage(''), 5000);
      loadConfigurations();
      resetForm();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create configuration');
    }
  };

  const handleUpdate = async () => {
    if (!token || !selectedConfig) return;

    try {
      const updateData: UpdateAIConfigurationInput = {
        name: formData.name,
        description: formData.description,
        model: formData.model,
        temperature: formData.temperature,
        max_tokens: formData.max_tokens,
        system_prompt: formData.system_prompt,
        user_prompt_template: formData.user_prompt_template,
        min_requirements: formData.min_requirements,
        max_requirements_per_type: formData.max_requirements_per_type,
        requirement_description_max_length: formData.requirement_description_max_length
      };

      await aiConfigService.updateConfiguration(selectedConfig.id, updateData, token);
      setShowEditModal(false);
      setSuccessMessage('Configuration updated successfully');
      setTimeout(() => setSuccessMessage(''), 5000);
      loadConfigurations();
      resetForm();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update configuration');
    }
  };

  const handleActivate = async (id: string) => {
    if (!token) return;

    try {
      await aiConfigService.activateConfiguration(id, token);
      setSuccessMessage('Configuration activated successfully');
      setTimeout(() => setSuccessMessage(''), 5000);
      loadConfigurations();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to activate configuration');
    }
  };

  const handleDelete = async (id: string, name: string) => {
    if (!token) return;

    if (!window.confirm(`Are you sure you want to delete "${name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await aiConfigService.deleteConfiguration(id, token);
      setSuccessMessage('Configuration deleted successfully');
      setTimeout(() => setSuccessMessage(''), 5000);
      loadConfigurations();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete configuration. Default configurations cannot be deleted.');
    }
  };

  const handleDuplicate = async (id: string, currentName: string) => {
    if (!token) return;

    const newName = window.prompt(`Enter name for duplicated configuration:`, `${currentName} (Copy)`);
    if (!newName) return;

    try {
      await aiConfigService.duplicateConfiguration(id, newName, token);
      setSuccessMessage('Configuration duplicated successfully');
      setTimeout(() => setSuccessMessage(''), 5000);
      loadConfigurations();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to duplicate configuration');
    }
  };

  const openCreateModal = () => {
    resetForm();
    setShowCreateModal(true);
  };

  const openEditModal = (config: AIConfiguration) => {
    setSelectedConfig(config);
    setFormData({
      name: config.name,
      description: config.description || '',
      configuration_type: config.configuration_type,
      model: config.model,
      temperature: config.temperature,
      max_tokens: config.max_tokens,
      system_prompt: config.system_prompt,
      user_prompt_template: config.user_prompt_template,
      min_requirements: config.min_requirements,
      max_requirements_per_type: config.max_requirements_per_type,
      requirement_description_max_length: config.requirement_description_max_length
    });
    setShowEditModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      configuration_type: 'executive_summary',
      model: 'gpt-3.5-turbo',
      temperature: 0.3,
      max_tokens: 2000,
      system_prompt: '',
      user_prompt_template: '',
      min_requirements: 3,
      max_requirements_per_type: 10,
      requirement_description_max_length: 200
    });
    setSelectedConfig(null);
  };

  return (
    <Layout>
      <div className="py-8 px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">AI Configuration</h1>
          <p className="mt-2 text-sm text-gray-600">
            Manage AI models, prompts, and processing rules for AI-powered features
          </p>
        </div>

        {/* Success Message */}
        {successMessage && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-md">
            <p className="text-sm text-green-800">{successMessage}</p>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {/* Create Button */}
        <div className="mb-6">
          <button
            onClick={openCreateModal}
            className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
          >
            + Create New Configuration
          </button>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
            <p className="mt-4 text-gray-600">Loading configurations...</p>
          </div>
        )}

        {/* Configuration Cards - Single Grid */}
        {!loading && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {configurations.map(config => (
              <div
                key={config.id}
                className={`bg-white rounded-lg shadow-md p-6 border-2 ${
                  config.is_active ? 'border-green-500' : 'border-gray-200'
                }`}
              >
                {/* Config Header */}
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{config.name}</h3>
                    {config.description && (
                      <p className="text-sm text-gray-600 mt-1">{config.description}</p>
                    )}
                  </div>
                  {config.is_active && (
                    <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded">
                      Active
                    </span>
                  )}
                </div>

                {/* Config Details */}
                <div className="space-y-2 mb-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Model:</span>
                    <span className="font-medium">{config.model}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Temperature:</span>
                    <span className="font-medium">{config.temperature}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Max Tokens:</span>
                    <span className="font-medium">{config.max_tokens}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Version:</span>
                    <span className="font-medium">v{config.version}</span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex flex-wrap gap-2 pt-4 border-t border-gray-200">
                  {!config.is_active && (
                    <button
                      onClick={() => handleActivate(config.id)}
                      className="px-3 py-1 text-sm bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors"
                    >
                      Activate
                    </button>
                  )}
                  <button
                    onClick={() => openEditModal(config)}
                    className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDuplicate(config.id, config.name)}
                    className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
                  >
                    Duplicate
                  </button>
                  {!config.is_default && (
                    <button
                      onClick={() => handleDelete(config.id, config.name)}
                      className="px-3 py-1 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors"
                    >
                      Delete
                    </button>
                  )}
                  {config.is_default && (
                    <span className="px-3 py-1 text-xs bg-gray-100 text-gray-500 rounded">
                      Default (Protected)
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Empty State */}
        {!loading && configurations.length === 0 && (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <p className="text-gray-600">No configurations found. Create your first one!</p>
          </div>
        )}

        {/* Create/Edit Modal */}
        {(showCreateModal || showEditModal) && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-10 mx-auto p-8 border w-full max-w-6xl shadow-2xl rounded-lg bg-white mb-10">
              {/* Modal Header */}
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">
                  {showCreateModal ? 'Create New Configuration' : 'Edit Configuration'}
                </h2>
                <button
                  onClick={() => { setShowCreateModal(false); setShowEditModal(false); }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Modal Body */}
              <div className="space-y-4" style={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}>
                {/* Name */}
                <div>
                  <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                    Name <span className="text-red-500">*</span>
                    <FieldInfo
                      title="Configuration Name"
                      description="A descriptive name for this AI configuration that helps you identify its purpose. This name will be displayed in the configuration list and helps distinguish between different AI setups."
                      examples={[
                        'Default Executive Summary',
                        'Premium Persona Generation',
                        'High-Quality Summary (GPT-4)',
                        'Fast Persona Gen (GPT-3.5)'
                      ]}
                      notes={[
                        'Choose a clear, descriptive name that indicates the configuration\'s purpose',
                        'Include the AI model name if you have multiple configs for the same feature',
                        'This name is only visible to Platform Admins'
                      ]}
                    />
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="e.g., Premium Executive Summary"
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                    Description
                    <FieldInfo
                      title="Configuration Description"
                      description="An optional detailed description explaining when and why this configuration should be used. This helps other administrators understand the purpose and intended use case for this configuration."
                      examples={[
                        'High-quality summaries for executive presentations',
                        'Fast persona generation for initial project planning',
                        'Detailed summaries including technical requirements',
                        'Diverse persona generation for B2B SaaS products'
                      ]}
                      notes={[
                        'Optional but recommended for clarity',
                        'Explain any special use cases or scenarios',
                        'Note if this is for testing or production use'
                      ]}
                    />
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="Brief description of this configuration"
                  />
                </div>

                {/* Configuration Type */}
                <div>
                  <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                    Configuration Type <span className="text-red-500">*</span>
                    <FieldInfo
                      title="Configuration Type"
                      description="Specifies which AI feature this configuration applies to. Each type uses the AI to generate different content for your projects."
                      examples={[
                        'Executive Summary - Generates project executive summaries',
                        'Persona Generation - Creates user personas based on requirements',
                        'Test Case Generation - Generates test cases (future feature)'
                      ]}
                      notes={[
                        'Cannot be changed after creation',
                        'Each feature type can have multiple configurations',
                        'Only one configuration per type can be active at a time',
                        'Active configuration is used for all AI generation requests'
                      ]}
                    />
                  </label>
                  <select
                    value={formData.configuration_type}
                    onChange={(e) => setFormData({ ...formData, configuration_type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    disabled={showEditModal} // Cannot change type on edit
                  >
                    <option value="executive_summary">Executive Summary</option>
                    <option value="persona_generation">Persona Generation</option>
                    <option value="test_case_generation">Test Case Generation</option>
                  </select>
                </div>

                {/* Model Settings */}
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                      Model
                      <FieldInfo
                        title="AI Model"
                        description="The OpenAI language model used to generate content. Different models have different capabilities, speeds, and costs."
                        examples={[
                          'GPT-3.5 Turbo - Fast, cost-effective, good for most uses',
                          'GPT-4 - Higher quality, slower, more expensive',
                          'GPT-4 Turbo - Best quality, fastest GPT-4, highest cost',
                          'GPT-4o - Supports JSON mode (required for persona generation)'
                        ]}
                        notes={[
                          'GPT-3.5 Turbo: ~$0.002 per 1K tokens (recommended for summaries)',
                          'GPT-4: ~$0.030 per 1K tokens (premium quality)',
                          'GPT-4 Turbo/4o: ~$0.010 per 1K tokens (balanced)',
                          'Persona generation REQUIRES GPT-4o or GPT-4-turbo for JSON support',
                          'Executive summaries work with any model'
                        ]}
                      />
                    </label>
                    <select
                      value={formData.model}
                      onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="gpt-3.5-turbo">GPT-3.5 Turbo</option>
                      <option value="gpt-4">GPT-4</option>
                      <option value="gpt-4-turbo-preview">GPT-4 Turbo</option>
                      <option value="gpt-4o">GPT-4o</option>
                    </select>
                  </div>
                  <div>
                    <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                      Temperature (0-2)
                      <FieldInfo
                        title="Temperature"
                        description="Controls the creativity and randomness of AI responses. Lower values make output more focused and deterministic, while higher values make it more creative and varied."
                        examples={[
                          '0.0-0.3: Very focused, consistent, deterministic output',
                          '0.3-0.7: Balanced creativity and consistency (recommended)',
                          '0.7-1.0: More creative and varied output',
                          '1.0-2.0: Highly creative, less predictable (experimental)'
                        ]}
                        notes={[
                          'Executive summaries: 0.5-0.7 (balanced, professional)',
                          'Persona generation: 0.85 (creative, diverse personas)',
                          'Lower values: Better for factual, consistent content',
                          'Higher values: Better for creative, varied content',
                          'Values above 1.5 may produce unrealistic results'
                        ]}
                      />
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      max="2"
                      value={formData.temperature}
                      onChange={(e) => setFormData({ ...formData, temperature: parseFloat(e.target.value) })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                      Max Tokens
                      <FieldInfo
                        title="Maximum Tokens"
                        description="The maximum number of tokens (roughly 4 characters or 3/4 of a word) the AI can generate in its response. This limits the length of generated content and controls costs."
                        examples={[
                          '500 tokens ≈ 375 words (short summary)',
                          '1000 tokens ≈ 750 words (medium summary)',
                          '1500 tokens ≈ 1125 words (detailed summary)',
                          '2000 tokens ≈ 1500 words (comprehensive)'
                        ]}
                        notes={[
                          'Recommended for executive summaries: 1000-1500 tokens',
                          'Recommended for persona generation: 1000-1500 tokens',
                          'Higher values cost more but allow longer responses',
                          'If output is cut off, increase this value',
                          'Input tokens (your data) don\'t count toward this limit'
                        ]}
                      />
                    </label>
                    <input
                      type="number"
                      value={formData.max_tokens}
                      onChange={(e) => setFormData({ ...formData, max_tokens: parseInt(e.target.value) })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </div>

                {/* Processing Rules */}
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                      Min Requirements
                      <FieldInfo
                        title="Minimum Requirements"
                        description="The minimum number of project requirements that must exist before AI generation is allowed. This ensures the AI has enough context to produce quality output."
                        examples={[
                          '0: Allow generation even with no requirements',
                          '3: Require at least 3 requirements (recommended)',
                          '5: Require substantial project definition',
                          '10: Require comprehensive requirements'
                        ]}
                        notes={[
                          'Recommended: 3-5 requirements for good results',
                          'Set to 0 to allow generation on new/minimal projects',
                          'More requirements = better AI context and output quality',
                          'If set too high, users may be blocked from using AI features'
                        ]}
                      />
                    </label>
                    <input
                      type="number"
                      value={formData.min_requirements}
                      onChange={(e) => setFormData({ ...formData, min_requirements: parseInt(e.target.value) })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                      Max Reqs Per Type
                      <FieldInfo
                        title="Maximum Requirements Per Type"
                        description="The maximum number of requirements of each type (functional, non-functional, etc.) to include when sending data to the AI. This prevents token limits from being exceeded on large projects."
                        examples={[
                          '5: Include up to 5 requirements per type',
                          '10: Include up to 10 requirements per type (recommended)',
                          '20: Include up to 20 requirements per type',
                          'Unlimited: Set very high (999) to include all'
                        ]}
                        notes={[
                          'Recommended: 10 per type (balances context and cost)',
                          'Higher values provide more context but cost more tokens',
                          'If a project has 50 functional requirements and this is set to 10, only the first 10 will be sent to AI',
                          'Helps prevent hitting token limits on large projects'
                        ]}
                      />
                    </label>
                    <input
                      type="number"
                      value={formData.max_requirements_per_type}
                      onChange={(e) => setFormData({ ...formData, max_requirements_per_type: parseInt(e.target.value) })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                      Desc Max Length
                      <FieldInfo
                        title="Description Maximum Length"
                        description="The maximum number of characters from each requirement description to send to the AI. Long descriptions are truncated to this length to control token usage and costs."
                        examples={[
                          '100 chars: Very brief, minimal context',
                          '200 chars: Short summary (recommended)',
                          '500 chars: Detailed context',
                          '1000 chars: Full descriptions (expensive)'
                        ]}
                        notes={[
                          'Recommended: 200-500 characters',
                          'Shorter = lower costs but less context for AI',
                          'Longer = better context but higher costs',
                          'If requirements have long descriptions, this prevents sending thousands of tokens',
                          'Example: "User must be able to..." (200 chars is about 1-2 sentences)'
                        ]}
                      />
                    </label>
                    <input
                      type="number"
                      value={formData.requirement_description_max_length}
                      onChange={(e) => setFormData({ ...formData, requirement_description_max_length: parseInt(e.target.value) })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </div>

                {/* System Prompt */}
                <div>
                  <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                    System Prompt <span className="text-red-500">*</span>
                    <FieldInfo
                      title="System Prompt"
                      description="Instructions that define the AI's role, expertise, and behavior. This sets the context for how the AI should respond and what persona it should adopt. The system prompt is sent with every request and shapes the AI's entire response style."
                      examples={[
                        'Executive Summary: "You are an expert business analyst who creates concise executive summaries..."',
                        'Persona Generation: "You are an expert UX researcher and persona designer who creates realistic user personas..."',
                        'Test Cases: "You are a senior QA engineer who writes comprehensive test cases..."'
                      ]}
                      notes={[
                        'Be specific about the AI\'s expertise and role',
                        'Define the output style and tone (professional, technical, etc.)',
                        'For persona generation: Emphasize diversity and realism',
                        'For summaries: Emphasize clarity and business focus',
                        'Keep it concise but comprehensive (2-4 sentences)',
                        'This is separate from the user prompt which contains project data'
                      ]}
                    />
                  </label>
                  <textarea
                    value={formData.system_prompt}
                    onChange={(e) => setFormData({ ...formData, system_prompt: e.target.value })}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 font-mono text-sm"
                    placeholder="You are an expert business analyst..."
                  />
                </div>

                {/* User Prompt Template */}
                <div>
                  <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                    User Prompt Template <span className="text-red-500">*</span>
                    <FieldInfo
                      title="User Prompt Template"
                      description="The template for the actual prompt sent to the AI containing project data. Use placeholders (in curly braces) that will be replaced with real project information when the AI is called. This is where you structure the specific request and provide context."
                      examples={[
                        'Executive Summary: "Generate a 3-paragraph executive summary for {projectName}, a {projectType} project..."',
                        'Persona: "Create a user persona for {projectName}. Requirements: {requirementsByType}..."'
                      ]}
                      notes={[
                        'Use placeholders to inject project data: {projectName}, {projectType}, {projectDescription}',
                        'Available placeholders: {projectName}, {projectType}, {projectDescription}, {requirementsByType}, {requirementCount}',
                        'For persona generation: Must specify exact dropdown values for age_range and gender',
                        'Be specific about format, length, and structure you want',
                        'Include instructions for output format (e.g., "Return JSON with fields: name, role, description...")',
                        'This template is filled with real data each time AI is called'
                      ]}
                    />
                  </label>
                  <textarea
                    value={formData.user_prompt_template}
                    onChange={(e) => setFormData({ ...formData, user_prompt_template: e.target.value })}
                    rows={8}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 font-mono text-sm"
                    placeholder="Use placeholders: {projectName}, {projectType}, {requirementsByType}..."
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Available placeholders: {'{projectName}'}, {'{projectType}'}, {'{projectDescription}'}, {'{startDate}'}, {'{targetCompletionDate}'}, {'{requirementCount}'}, {'{requirementsByType}'}
                  </p>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="mt-6 flex justify-end gap-3">
                <button
                  onClick={() => { setShowCreateModal(false); setShowEditModal(false); }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={showCreateModal ? handleCreate : handleUpdate}
                  disabled={!formData.name || !formData.system_prompt || !formData.user_prompt_template}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  {showCreateModal ? 'Create Configuration' : 'Update Configuration'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default AIConfigurationPage;
